<template>
  <AdminAppLayout title="HRDOS | Add Employee"> 
    <div class="card z-depth-1" style="padding: 40px; border-radius: 30px">
        <h5 class="center-align blue-text" id="stepTitle" style="margin-bottom: 20px">
            Step {{ currentStep }}/{{ totalSteps }} — {{ stepTitles[currentStep] }}
        </h5>

        <div class="progress" style="height: 4px; border-radius: 2px;">
            <div class="determinate" id="progressBar" :style="{width: (currentStep/totalSteps)*100 + '%'}"></div>
        </div>
        
        <form @submit.prevent="addEmployee()">
            <div class="step" v-show="currentStep === 1">
                <div class="row">
                    <div class="divider" style="margin-bottom: 25px"></div>

                    <div class="col s12 center-align" style="margin-bottom: 35px">
                        <img id="photoPreview" :src="photoPreview"
                            style="
                                width: 160px;
                                height: 160px;
                                object-fit: cover;
                                border-radius: 50%;
                                display: block;
                                margin: 0 auto 15px auto;
                                box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                            ">
                        <input type="file" name="profile_photo" ref="profile_photo" @change="handlePhotoUpload" accept="image/*"
                            style="display: none">

                        <button type="button" class="btn blue lighten-1"
                            style="
                                display: flex;
                                justify-content: center;
                                align-items: center;
                                margin: 0 auto;
                                border-radius: 20px;
                            "
                            @click="$refs.profile_photo.click()">
                            <i style="margin-right: 8px" class="material-icons">add_photo_alternate</i>
                            <b>Upload Photo</b>
                        </button>
                        <span style="color: red; display: block; margin-top: 8px;" v-if="errors.profile_photo">{{ errors.profile_photo }}</span>
                        <span style="display: block; margin-top: 8px; color: #9e9e9e;" v-else>MAX 2MB</span>
                    </div>

                    <div class="input-field col s12 m4">
                        <input :class="{ invalid: errors.username }" class="validate" ref="username" id="username" type="text" v-model="employee.username" @input="errors.username=''" autocomplete="off" required>
                        <label for="username">Username</label>
                        <span class="helper-text left-align" :data-error="errors.username"></span>
                    </div>

                    <div class="input-field col s12 m4">
                        <input :class="{ invalid: errors.email }" class="validate" ref="email" id="email" type="email" v-model="employee.email" @input="errors.email=''" required>
                        <label for="email">Email</label>
                        <span class="helper-text left-align" :data-error="errors.email"></span>
                    </div>

                    <div class="input-field col s12 m4">
                        <input :class="{ invalid: errors.cellphone_number }" class="validate" ref="cellphone_number" id="cellphone_number" type="text" v-model="employee.cellphone_number" @input="employee.cellphone_number = formatNumber(employee.cellphone_number, '0000-000-0000'); errors.cellphone_number=''" required minlength="13" maxlength="13">
                        <label for="cellphone_number">CP Number</label>
                        <span class="helper-text left-align" :data-error="errors.cellphone_number"></span>
                    </div>

                    <div class="input-field col s12 m4">
                        <input :class="{ invalid: errors.first_name }" class="validate" ref="first_name" id="first_name" type="text" v-model="employee.first_name" @input="capitalize('first_name'); errors.first_name=''" required>
                        <label for="first_name">First Name</label>
                        <span class="helper-text left-align" :data-error="errors.first_name"></span>
                    </div>

                    <div class="input-field col s12 m4">
                        <input :class="{ invalid: errors.middle_name }" class="validate" ref="middle_name" id="middle_name" type="text" v-model="employee.middle_name" @input="capitalize('middle_name'); errors.middle_name=''">
                        <label for="middle_name">Middle Name</label>
                        <span class="helper-text left-align" :data-error="errors.middle_name"></span>
                    </div>

                    <div class="input-field col s12 m4">
                        <input :class="{ invalid: errors.last_name }" class="validate" ref="last_name" id="last_name" type="text" v-model="employee.last_name" @input="capitalize('last_name'); errors.last_name=''" required>
                        <label for="last_name">Last Name</label>
                        <span class="helper-text left-align" :data-error="errors.last_name"></span>
                    </div>

                    <div class="input-field col s12 m6">
                        <input :class="{'invalid':errors.birthday, 'valid':!errors.birthday && employee.birthday}" id="birthday" ref="birthday" type="date" v-model="employee.birthday" @change="errors.birthday=''" required>
                        <label for="birthday" class="active">Birth Date</label>
                        <span class="helper-text left-align" :data-error="errors.birthday"></span>
                    </div>

                    <div class="input-field col s12 m6" :class="{'has-error': errors.sex, 'is-valid': employee.sex && !errors.sex}">
                        <select ref="sex" id="sex" v-model="employee.sex" @change="errors.sex=''" required>
                            <option value="" disabled selected>Select Sex</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                        </select>
                        <label for="sex">Sex</label>
                        <span class="helper-text left-align red-text" v-show="errors.sex" style="font-size: 12px; position: absolute;">{{ errors.sex }}</span>
                    </div>
                </div>

                <div class="row" style="margin-top: 30px;">
                    <div class="col s12 m6">
                        <div class="card-panel z-depth-0" style="padding: 25px; border-radius: 15px; height: 100%; border: 1px solid #e0e0e0;">
                            <h6 style="text-align: center; font-weight: bold; margin-bottom: 30px;" class="blue-text">
                                Permanent / Current Address
                            </h6>
                            
                            <div class="input-field">
                                <input :class="{ invalid: errors.region }" class="validate" type="text" id="region" ref="region" list="region-list" v-model="employee.region" @input="errors.region=''" @blur="handleRegionChange('current')" autocomplete="off" placeholder="Select Region" required>
                                <label for="region" class="active">Region</label>
                                <datalist id="region-list">
                                    <option v-for="r in regions" :value="r.name" :key="r.code"></option>
                                </datalist>
                                <span class="helper-text left-align" :data-error="errors.region"></span>
                            </div>

                            <div class="input-field">
                                <input :class="{ invalid: errors.province }" class="validate" type="text" id="province" ref="province" list="province-list" v-model="employee.province" @input="errors.province=''" @blur="handleProvinceChange('current')" autocomplete="off" :disabled="!employee.region" :placeholder="!employee.region ? 'Select Region first...' : 'Select Province'" required>
                                <label for="province" class="active">Province</label>
                                <datalist id="province-list">
                                    <option v-for="p in provinces" :value="p.name" :key="p.code"></option>
                                </datalist>
                                <span class="helper-text left-align" :data-error="errors.province"></span>
                            </div>

                            <div class="input-field">
                                <input :class="{ invalid: errors.city_or_municipality }" class="validate" type="text" id="city_or_municipality" ref="city_or_municipality" list="city-list" v-model="employee.city_or_municipality" @input="errors.city_or_municipality=''" @blur="handleCityChange('current')" autocomplete="off" :disabled="!employee.province" :placeholder="!employee.province ? 'Select Province first...' : 'Select City/Municipality'" required>
                                <label for="city_or_municipality" class="active">City or Municipality</label>
                                <datalist id="city-list">
                                    <option v-for="c in cities_or_municipalities" :value="c.name" :key="c.code"></option>
                                </datalist>
                                <span class="helper-text left-align" :data-error="errors.city_or_municipality"></span>
                            </div>

                            <div class="input-field">
                                <input :class="{ invalid: errors.barangay }" class="validate" type="text" id="barangay" ref="barangay" list="brgy-list" v-model="employee.barangay" @input="errors.barangay=''" autocomplete="off" :disabled="!employee.city_or_municipality" :placeholder="!employee.city_or_municipality ? 'Select City/Municipality first...' : 'Select Barangay'" required>
                                <label for="barangay" class="active">Barangay</label>
                                <datalist id="brgy-list">
                                    <option v-for="b in barangays" :value="b.name" :key="b.code"></option>
                                </datalist>
                                <span class="helper-text left-align" :data-error="errors.barangay"></span>
                            </div>

                            <div class="input-field">
                                <input id="address_line" type="text" v-model="employee.address_line" placeholder="Street, House No., Subd., etc.">
                                <label for="address_line" class="active">Address Line (Optional)</label>
                            </div>
                        </div>
                    </div><div class="col s12 m6">
                        <div class="card-panel z-depth-0" style="padding: 25px; border-radius: 15px; height: 100%; border: 1px solid #e0e0e0;">
                            <h6 style="text-align: center; font-weight: bold; margin-bottom: 30px;" class="blue-text">
                                Birthplace
                            </h6>
                            
                            <div class="input-field">
                                <input :class="{ invalid: errors.bp_region }" class="validate" type="text" id="bp_region" ref="bp_region" list="bp_region-list" v-model="employee.bp_region" @input="errors.bp_region=''" @blur="handleRegionChange('bp')" autocomplete="off" placeholder="Select Region" required>
                                <label for="bp_region" class="active">Region</label>
                                <datalist id="bp_region-list">
                                    <option v-for="r in regions" :value="r.name" :key="r.code"></option>
                                </datalist>
                                <span class="helper-text left-align" :data-error="errors.bp_region"></span>
                            </div>

                            <div class="input-field">
                                <input :class="{ invalid: errors.bp_province }" class="validate" type="text" id="bp_province" ref="bp_province" list="bp_province-list" v-model="employee.bp_province" @input="errors.bp_province=''" @blur="handleProvinceChange('bp')" autocomplete="off" :disabled="!employee.bp_region" :placeholder="!employee.bp_region ? 'Select Region first...' : 'Select Province'" required>
                                <label for="bp_province" class="active">Province</label>
                                <datalist id="bp_province-list">
                                    <option v-for="p in bp_provinces" :value="p.name" :key="p.code"></option>
                                </datalist>
                                <span class="helper-text left-align" :data-error="errors.bp_province"></span>
                            </div>

                            <div class="input-field">
                                <input :class="{ invalid: errors.bp_city_or_municipality }" class="validate" type="text" id="bp_city_or_municipality" ref="bp_city_or_municipality" list="bp_city-list" v-model="employee.bp_city_or_municipality" @input="errors.bp_city_or_municipality=''" @blur="handleCityChange('bp')" autocomplete="off" :disabled="!employee.bp_province" :placeholder="!employee.bp_province ? 'Select Province first...' : 'Select City/Municipality'" required>
                                <label for="bp_city_or_municipality" class="active">City or Municipality</label>
                                <datalist id="bp_city-list">
                                    <option v-for="c in bp_cities" :value="c.name" :key="c.code"></option>
                                </datalist>
                                <span class="helper-text left-align" :data-error="errors.bp_city_or_municipality"></span>
                            </div>

                            <div class="input-field">
                                <input :class="{ invalid: errors.bp_barangay }" class="validate" type="text" id="bp_barangay" ref="bp_barangay" list="bp_brgy-list" v-model="employee.bp_barangay" @input="errors.bp_barangay=''" autocomplete="off" :disabled="!employee.bp_city_or_municipality" :placeholder="!employee.bp_city_or_municipality ? 'Select City/Municipality first...' : 'Select Barangay'" required>
                                <label for="bp_barangay" class="active">Barangay</label>
                                <datalist id="bp_brgy-list">
                                    <option v-for="b in bp_barangays" :value="b.name" :key="b.code"></option>
                                </datalist>
                                <span class="helper-text left-align" :data-error="errors.bp_barangay"></span>
                            </div>

                            <div class="input-field">
                                <input id="bp_address_line" type="text" v-model="employee.bp_address_line" placeholder="Street, House No., Subd., etc.">
                                <label for="bp_address_line" class="active">Address Line (Optional)</label>
                            </div>
                        </div>
                    </div></div>
            </div>
            
            <div class="step" v-show="currentStep === 2">
                <div class="divider" style="margin-bottom: 25px"></div>
                    
                    <div id="stepContent">
                        <div class="row">
                            <div class="input-field col s12" :class="{'has-error': errors.employment_status_id, 'is-valid': employee.employment_status_id && !errors.employment_status_id}">
                                <select ref="employment_status_id" id="employment_status" v-model="employee.employment_status_id" @change="errors.employment_status_id=''" required>
                                    <option value="" disabled selected>Select Employment Status</option>
                                    <option v-for="employmentStatus in dropdowns.employmentStatuses" 
                                    :key="employmentStatus.id"
                                    :value="employmentStatus.id">{{ employmentStatus.name }}</option>
                                </select>
                                <label for="employment_status">Status</label>
                                <span class="helper-text left-align red-text" v-show="errors.employment_status_id" style="font-size: 12px; position: absolute;">{{ errors.employment_status_id }}</span>
                            </div>

                            <div class="input-field col s12 m6"
                                style="
                                    display: flex;
                                    align-items: center;
                                    transition: all 0.2s ease;
                                    position: relative;
                                ">
                                <span
                                    style="
                                        padding-right: 6px;
                                        color: #424242;
                                        font-size: 1.3rem;
                                        margin-bottom: 0;
                                        white-space: nowrap;
                                    ">{{ employee.employment_status_id==3 ? 'LTO-COS-' : 'LTO-RO2-' }}
                                </span>
                                <input :class="{ invalid: errors.employee_id_number }" class="validate" type="text" v-model="employee.employee_id_number" ref="employee_id_number" id="lto_id" placeholder="LTO ID"
                                    required @input="employee.employee_id_number = formatNumber(employee.employee_id_number, '000'); errors.employee_id_number=''"
                                    style="
                                        margin: 0 !important;
                                        height: 3rem;
                                        flex: 1;
                                        font-size: 1.2rem;
                                    ">
                                <label class="active" for="lto_id">Employee ID Number</label>
                                <span class="helper-text left-align" :data-error="errors.employee_id_number"
                                    style="
                                    position: absolute;
                                    top: 100%;
                                    left: 10px;
                                    width: 100%;
                                "></span>
                            </div>

                            <div v-if="employee.employment_status_id != 3" class="input-field col s12 m6" :class="{'has-error': errors.plantilla_id, 'is-valid': (employee.plantilla_id || employee.employment_status_id == 3) && !errors.plantilla_id}">
                                <select ref="plantilla_id" id="plantilla" v-model="employee.plantilla_id" @change="errors.plantilla_id=''" :required="employee.employment_status_id != 3" :disabled="employee.employment_status_id == 3">
                                    <option value="" disabled selected>{{employee.employment_status_id != 3 ? 'Select Plantilla' : '--------------------------------------------------------------------------'}}</option>
                                    <option v-for="plantilla in dropdowns.plantillas"
                                    :key="plantilla.id"
                                    :value="plantilla.id">{{ plantilla.name }}</option>
                                </select>
                                <label for="plantilla">Plantilla</label>
                                <span class="helper-text left-align red-text" v-show="errors.plantilla_id" style="font-size: 12px; position: absolute;">{{ errors.plantilla_id }}</span>
                            </div>

                            <div class="input-field col s12 m6"
                                style="
                                    display: flex;
                                    align-items: center;
                                    transition: all 0.2s ease;
                                    position: relative;
                                ">
                                <span id="itemPreset"
                                    v-show="employee.employment_status_id !== 3"
                                    style="
                                        padding-right: 6px;
                                        color: #424242;
                                        font-size: 1.2rem;
                                        margin-bottom: 0;
                                        white-space: nowrap;
                                    ">OSEC-DOTrB-
                                </span>
                                <input :class="{'invalid': errors.item_number, 'valid':employee.employment_status_id==3, 'validate':employee.employment_status_id!=3}" type="text" ref="item_number" id="item_number" v-model="employee.item_number" :placeholder="employee.employment_status_id == 3 ? 'N/A' : 'Item Number'"
                                    @input="errors.item_number=''"
                                    :required="employee.employment_status_id !== 3"
                                    :disabled="employee.employment_status_id==3"
                                    style="
                                        margin: 0 !important;
                                        height: 3rem;
                                        flex: 1;
                                        font-size: 1.2rem;
                                    ">
                                <label class="active" for="item_number">Item Number</label>
                                <span class="helper-text left-align" :data-error="errors.item_number"
                                    style="
                                    position: absolute;
                                    top: 100%;
                                    left: 10px;
                                    width: 100%;
                                "></span>
                            </div>

                            <div class="input-field col s12 m6" :class="{'has-error': errors.eligibility_id, 'is-valid': employee.eligibility_id && !errors.eligibility_id}">
                                <select ref="eligibility_id" id="eligibility" v-model="employee.eligibility_id" @change="errors.eligibility_id=''" required>
                                    <option value="" selected>Select Civil Service Eligibility</option>
                                    <option v-for="eligibility in dropdowns.eligibilities"
                                    :key="eligibility.id"
                                    :value="eligibility.id">{{ eligibility.name }}</option>
                                </select>
                                <label for="eligibility">Eligibility</label>
                                <span class="helper-text left-align red-text" v-show="errors.eligibility_id" style="font-size: 12px; position: absolute;">{{ errors.eligibility_id }}</span>
                            </div>
                            
                            <div v-if="employee.employment_status_id != 3" class="input-field col s12 m6" :class="{'has-error': errors.position_id, 'is-valid': (employee.position_id || employee.employment_status_id == 3) && !errors.position_id}">
                                <select ref="position_id" id="job_title" v-model="employee.position_id" @change="handlePositionChange(employee.position_id); errors.position_id=''" :required="employee.employment_status_id != 3" :disabled="employee.employment_status_id == 3">
                                    <option value="" disabled selected>{{employee.employment_status_id != 3 ? 'Select Position Title' : '--------------------------------------------------------------------------'}}</option>
                                    <option v-for="position in dropdowns.positions"
                                    :key="position.id"
                                    :value="position.id">{{ position.title }}</option>
                                </select>
                                <label for="job_title">Position Title</label>
                                <span class="helper-text left-align red-text" v-show="errors.position_id" style="font-size: 12px; position: absolute;">{{ errors.position_id }}</span>
                            </div>

                            <div class="input-field col s12 m6" :class="{'has-error': errors.assigned_office_id, 'is-valid': employee.assigned_office_id && !errors.assigned_office_id}">
                                <select ref="assigned_office_id" id="current_office_assigned" v-model="employee.assigned_office_id" @change="errors.assigned_office_id=''" required>
                                    <option value="" disabled selected>Select Current Office Assigned</option>
                                    <option v-for="office in dropdowns.offices"
                                    :key="office.id"
                                    :value="office.id">{{ office.name }}</option>
                                </select>
                                <label for="current_office_assigned">Current Office Assigned</label>
                                <span class="helper-text left-align red-text" v-show="errors.assigned_office_id" style="font-size: 12px; position: absolute;">{{ errors.assigned_office_id }}</span>

                            </div>

                            <div id="sgDiv" class="input-field col s12 m2" v-show="employee.employment_status_id != 3">
                                <input :class="{ valid: employee.salary_grade_display }" ref="base_salary" id="base_salary" type="text" step="1.00" :value="employee.position_id ? 'SG ' + employee.salary_grade_display : 'N/A'" placeholder="N/A" readonly>
                                
                                <label class="active" for="salary_grade_display">Salary Grade</label>
                                <span class="helper-text left-align" v-show="!employee.position_id"></span>
                                <input type="hidden" v-model="employee.salary_grade" id="salary_grade">
                            </div>
                            <div id="ssDiv" class="input-field col s12 m2" v-show="employee.employment_status_id != 3" :class="{'has-error': errors.salary_step, 'is-valid': employee.salary_step && !errors.salary_step}">
                                <select :style="!employee.salary_grade ? 'pointer-events: none; background-color: #f4f4f4;' : ''" ref="salary_step" id="salary_step" v-model="employee.salary_step" @change="errors.salary_step=''; loadSalaryAmount()" :disabled="!employee.salary_grade" :required="employee.employment_status_id != 3">
                                    <option value="" disabled selected>Select Salary Step</option>
                                    <option v-for="salaryStep in dropdowns.salarySteps"
                                    :key="salaryStep.step"
                                    :value="salaryStep.step">STEP {{ salaryStep.step }}</option>
                                </select>
                                <label for="salary_step">Salary Step</label>
                                <span class="helper-text left-align red-text" v-show="errors.salary_step" style="font-size: 12px; position: absolute;">{{ errors.salary_step }}</span>
                            </div>
                            <div id="saDiv" class="input-field col s12 m2" v-show="employee.employment_status_id != 3">
                                <span style="
                                        position: absolute; 
                                        left: 5px; 
                                        top: 10px;
                                        color: #424242; 
                                        font-size: 1.2rem; 
                                        z-index: 1;
                                    ">₱</span>

                                <input :class="{ valid: salaryAmount }" ref="base_salary" id="base_salary" type="text" :value="formatCurrency(salaryAmount)" placeholder="00.00" readonly
                                    style="
                                        padding-left: 15px !important; 
                                        margin: 0 !important; 
                                        height: 3rem; 
                                        font-size: 1.2rem;
                                        box-sizing: border-box;
                                    ">

                                <label class="active" for="base_salary">Base Salary</label>

                                <span class="helper-text left-align" :data-error="errors.base_salary" style="position: absolute; top: 100%; left: 10px; width: 100%;"></span>
                            </div>
                            
                            <div id="bsDiv" class="input-field col s12 m6" v-show="employee.employment_status_id === 3" style="transition: all 0.2s ease; position: relative;">
                                <span style="
                                        position: absolute; 
                                        left: 5px; 
                                        top: 10px;
                                        color: #424242; 
                                        font-size: 1.2rem; 
                                        z-index: 1;
                                    ">₱</span>

                                <input :class="{ 'invalid': errors.base_salary }" ref="base_salary_manual" id="base_salary_manual" type="number" step="1.00" v-model="employee.base_salary" @input="errors.base_salary=''" placeholder="00.00" :required="employee.employment_status_id == 3"
                                    style="
                                        padding-left: 15px !important; 
                                        margin: 0 !important; 
                                        height: 3rem; 
                                        font-size: 1.2rem;
                                        box-sizing: border-box;
                                    ">

                                <label class="active" for="base_salary_manual">Base Salary</label>

                                <span class="helper-text left-align" :data-error="errors.base_salary" style="position: absolute; top: 100%; left: 10px; width: 100%;"></span>
                            </div>

                            <div class="input-field col s12 m6">
                                <input :class="{'invalid':errors.effectivity_date, 'valid':!errors.effectivity_date && employee.effectivity_date}" id="effectivity_date" ref="effectivity_date" type="date" v-model="employee.effectivity_date" @change="errors.effectivity_date=''">
                                <label v-if="employee.employment_status_id != 3" for="effectivity_date" class="active">Employment Date</label>
                                <label v-else for="effectivity_date" class="active">Date hired</label>
                                <span class="helper-text left-align" :data-error="errors.effectivity_date"></span>
                            </div>

                        </div> 

                        
                        <div class="row">
                            <h5 style="text-align: center; margin-top: 25px">Government IDs</h5>
                            
                            <!-- Dynamic Col width if GSIS is hidden -->
                            <div class="input-field" :class="employee.employment_status_id == 3 ? 'col s12 m6' : 'col s12 m4'">
                                <input :class="{ invalid: errors.tin }" class="validate" ref="tin" id="tin" type="text" v-model="employee.tin" @input="employee.tin = formatNumber(employee.tin, '000-000-000-000'); errors.tin=''" minlength="15" maxlength="15">
                                <label for="tin">TIN</label>
                                <span class="helper-text left-align" :data-error="errors.tin" style="position: absolute; width: 100%"></span>
                            </div>

                            <div class="input-field col s12 m4" v-show="employee.employment_status_id != 3">
                                <input :required="employee.employment_status_id !== 3" :class="{ invalid: errors.gsis}" class="validate" ref="gsis" id="gsis" type="text" v-model="employee.gsis" @input="employee.gsis = formatNumber(employee.gsis, '00000000000'); errors.gsis=''" minlength="11" maxlength="11">
                                <label :class="{ active: employee.gsis }" for="gsis">GSIS Number</label>
                                <span class="helper-text left-align" :data-error="errors.gsis" style="position: absolute; width: 100%"></span>
                            </div>
                            
                            <div class="input-field" :class="employee.employment_status_id == 3 ? 'col s12 m6' : 'col s12 m4'">
                                <input :required="employee.employment_status_id === 3" :class="{ invalid: errors.sss }" class="validate" ref="sss" id="sss" type="text" v-model="employee.sss" @input="employee.sss = formatNumber(employee.sss, '00-0000000-0'); errors.sss=''" minlength="12" maxlength="12">
                                <label :class="{ active: employee.sss }" for="sss">SSS Number</label>
                                <span class="helper-text left-align" :data-error="errors.sss" style="position: absolute; width: 100%"></span>
                            </div>

                            <div class="input-field col s12 m6">
                                <input :class="{ invalid: errors.pagibig }" class="validate" ref="pagibig" id="pagibig" type="text" v-model="employee.pagibig" @input="employee.pagibig = formatNumber(employee.pagibig, '0000-0000-0000'); errors.pagibig=''" required minlength="14" maxlength="14">
                                <label for="pagibig">Pag-IBIG Number</label>
                                <span class="helper-text left-align" :data-error="errors.pagibig"></span>
                            </div>
                            <div class="input-field col s12 m6">
                                <input :class="{ invalid: errors.philhealth }" class="validate" ref="philhealth" id="philhealth" type="text" v-model="employee.philhealth" @input="employee.philhealth = formatNumber(employee.philhealth, '00-000000000-0'); errors.philhealth=''" required minlength="14" maxlength="14">
                                <label for="philhealth">PhilHealth Number</label>
                                <span class="helper-text left-align" :data-error="errors.philhealth"></span>
                            </div>
                        </div>
                    </div>
            </div>

            <div class="step" v-show="currentStep === 3">
                <div class="divider" style="margin-bottom: 25px"></div>
                <h5 class="center-align" style="margin-bottom: 30px; font-weight: 500;">Review Employee Details</h5>

                <div class="row">
                    <div class="col s12 center-align" style="margin-bottom: 30px;">
                        <img :src="photoPreview" style="width: 140px; height: 140px; object-fit: cover; border-radius: 50%; border: 4px solid #fff; box-shadow: 0 4px 10px rgba(0,0,0,0.15);">
                        <h5 style="margin-top: 15px; font-weight: 600;" class="blue-text text-darken-2">
                            {{ employee.first_name }} {{ employee.middle_name }} {{ employee.last_name }}
                        </h5>
                        <p class="grey-text text-darken-1" style="font-size: 1.1rem; margin-top: -5px; margin-bottom: 5px;">
                            {{ getDisplayValue('positions', employee.position_id, 'title') }}
                        </p>
                        <div style="display: inline-block; padding: 4px 12px; border-radius: 20px;" class="blue lighten-5 blue-text text-darken-3 bold">
                            <span v-if="employee.employee_id_number">
                                {{ employee.employment_status_id == 3 ? 'LTO-COS-' : 'LTO-RO2-' }}{{ employee.employee_id_number }}
                            </span>
                            <span v-else>ID Pending</span>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col s12 m6">
                        <div class="card-panel z-depth-0" style="border: 1px solid #e0e0e0; border-radius: 12px; height: 100%;">
                            <h6 class="blue-text text-darken-2" style="font-weight: bold; border-bottom: 1px solid #eee; padding-bottom: 10px;">Contact & Account</h6>
                            <table class="striped clean-table">
                                <tbody>
                                    <tr><th style="width: 40%" class="grey-text">Username</th><td><strong>{{ employee.username || 'N/A' }}</strong></td></tr>
                                    <tr><th class="grey-text">Email Address</th><td><strong>{{ employee.email || 'N/A' }}</strong></td></tr>
                                    <tr><th class="grey-text">Mobile Number</th><td><strong>{{ employee.cellphone_number || 'N/A' }}</strong></td></tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class="col s12 m6">
                        <div class="card-panel z-depth-0" style="border: 1px solid #e0e0e0; border-radius: 12px; height: 100%;">
                            <h6 class="blue-text text-darken-2" style="font-weight: bold; border-bottom: 1px solid #eee; padding-bottom: 10px;">Personal Details</h6>
                            <table class="striped clean-table">
                                <tbody>
                                    <tr><th style="width: 40%" class="grey-text">Sex</th><td style="text-transform: capitalize;"><strong>{{ employee.sex || 'N/A' }}</strong></td></tr>
                                    <tr><th class="grey-text">Birthday</th><td><strong>{{ employee.birthday || 'N/A' }}</strong></td></tr>
                                    <tr><th class="grey-text">Birthplace</th><td><strong>{{ employee.birthplace || 'N/A' }}</strong></td></tr>
                                    <tr><th class="grey-text">Address</th><td><strong>{{ employee.address || 'N/A' }}</strong></td></tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col s12 m6">
                        <div class="card-panel z-depth-0" style="border: 1px solid #e0e0e0; border-radius: 12px; height: 100%;">
                            <h6 class="green-text text-darken-2" style="font-weight: bold; border-bottom: 1px solid #eee; padding-bottom: 10px;">Employment Profile</h6>
                            <table class="striped clean-table">
                                <tbody>
                                    <tr><th style="width: 40%" class="grey-text">Status</th><td><strong>{{ getDisplayValue('employmentStatuses', employee.employment_status_id) }}</strong></td></tr>
                                    <tr v-if="employee.employment_status_id != 3"><th class="grey-text">Position</th><td><strong>{{ getDisplayValue('positions', employee.position_id) }}</strong></td></tr>
                                    <tr v-if="employee.employment_status_id != 3"><th class="grey-text">Plantilla</th><td><strong>{{ getDisplayValue('plantillas', employee.plantilla_id) }}</strong></td></tr>
                                    <tr v-if="employee.employment_status_id != 3"><th class="grey-text">Item Number</th><td><strong>OSEC-DOTrB-{{ employee.item_number || 'N/A' }}</strong></td></tr>
                                    <tr><th class="grey-text">Eligibility</th><td><strong>{{ getDisplayValue('eligibilities', employee.eligibility_id) }}</strong></td></tr>
                                    <tr><th class="grey-text">Assigned Office</th><td><strong>{{ getDisplayValue('offices', employee.assigned_office_id) }}</strong></td></tr>
                                    <tr><th class="grey-text">{{ employee.employment_status_id == 3 ? 'Date Hired' : 'Effectivity Date' }}</th><td><strong>{{ employee.effectivity_date || 'N/A' }}</strong></td></tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class="col s12 m6">
                        <div class="card-panel z-depth-0" style="border: 1px solid #e0e0e0; border-radius: 12px; margin-bottom: 20px;">
                            <h6 class="green-text text-darken-2" style="font-weight: bold; border-bottom: 1px solid #eee; padding-bottom: 10px;">Compensation</h6>
                            <table class="striped clean-table">
                                <tbody>
                                    <tr>
                                        <th style="width: 40%" class="grey-text">Base Salary</th>
                                        <td>
                                            <strong>₱{{ formatCurrency(employee.employment_status_id == 3 ? employee.base_salary : salaryAmount) }}</strong>
                                        </td>
                                    </tr>
                                    <tr v-if="employee.employment_status_id != 3">
                                        <th class="grey-text">Grade / Step</th>
                                        <td><strong>SG {{ employee.salary_grade }}, Step {{ employee.salary_step }}</strong></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div class="card-panel z-depth-0" style="border: 1px solid #e0e0e0; border-radius: 12px; margin-bottom: 0;">
                            <h6 class="orange-text text-darken-2 center-align" style="font-weight: bold; border-bottom: 1px solid #eee; padding-bottom: 10px;">Government IDs</h6>
                            
                            <div class="row center-align" style="margin-bottom: 15px; margin-top: 15px;">
                                <div :class="employee.employment_status_id == 3 ? 'col s6' : 'col s4'">
                                    <span class="grey-text" style="font-size: 0.8rem; display: block;">TIN</span>
                                    <strong>{{ employee.tin || 'N/A' }}</strong>
                                </div>
                                <div class="col s4" v-show="employee.employment_status_id != 3">
                                    <span class="grey-text" style="font-size: 0.8rem; display: block;">GSIS</span>
                                    <strong>{{ employee.gsis || 'N/A' }}</strong>
                                </div>
                                <div :class="employee.employment_status_id == 3 ? 'col s6' : 'col s4'">
                                    <span class="grey-text" style="font-size: 0.8rem; display: block;">SSS</span>
                                    <strong>{{ employee.sss || 'N/A' }}</strong>
                                </div>
                            </div>
                            
                            <div class="row center-align" style="margin-bottom: 10px;">
                                <div class="col s6">
                                    <span class="grey-text" style="font-size: 0.8rem; display: block;">Pag-IBIG</span>
                                    <strong>{{ employee.pagibig || 'N/A' }}</strong>
                                </div>
                                <div class="col s6">
                                    <span class="grey-text" style="font-size: 0.8rem; display: block;">PhilHealth</span>
                                    <strong>{{ employee.philhealth || 'N/A' }}</strong>
                                </div>
                            </div>
                        </div>
                        </div>
                </div>
            </div>

            <div class="right-align" style="margin-top: 30px; display: flex; justify-content: flex-end; align-items: center; gap: 15px;">
                <button @click="currentStep--" type="button" class="btn-flat" id="prevBtn" v-show="currentStep !== 1" :disabled="nextStep">
                    Back
                </button>
                <button @click="validateStep()" type="button" class="btn blue lighten-1 btn-animated" id="nextBtn" v-show="currentStep < totalSteps" style="border-radius: 20px; padding: 0 30px; min-width: 120px;" :disabled="nextStep">
                    <span v-if="!nextStep"><b>Next</b></span>
                    <span v-else class="dots-loader">
                        <span>.</span><span>.</span><span>.</span>
                    </span>
                </button>
                <button type="submit" class="btn black btn-animated" id="submitBtn" v-show="currentStep === totalSteps" style="border-radius: 20px; padding: 0 30px; min-width: 180px;" :disabled="loading">
                    <span v-if="!loading">Save Employee</span>
                    <span v-else class="dots-loader">
                        <span>.</span><span>.</span><span>.</span>
                    </span>
                </button>
            </div>
        </form>
        
    </div>
  </AdminAppLayout>
</template>

<script>
import {api, externalApi} from "@/axios";
import AdminAppLayout from "../../layout/AdminAppLayout.vue";
import defaultPic from '@/assets/picDefault.png';
import { refreshSelectDropdown } from "@/m-css";

export default {
  name: "EmployeesView",
  components: {
    AdminAppLayout,
  },
  data() {
    return {
      currentStep: 1,
      totalSteps: 3,
      stepTitles: {
        1: "Employee Account and Personal Information",
        2: "Employee Employment Details",
        3: "Review and Confirmation",
      },
      loading: false,
      photoPreview: defaultPic,
      //for employee details
      employee: {
        //step 1
        profile_photo: null,
        username: "",
        email: "",
        cellphone_number: "",
        first_name: "",
        middle_name: "",
        last_name: "",
        birthday: "",
        sex: "",

        // =============================================================
            //for address - current/permanent
            region: "",
            province: "",
            city_or_municipality: "",
            barangay: "",
            address_line: "",
            //for birthplace
            bp_region: "",
            bp_province: "",
            bp_city_or_municipality: "",
            bp_barangay: "",
            bp_address_line: "",
        // =============================================================
        address: "",
        birthplace: "",
        
        //step 2
        employment_status_id: "",
        employee_id_number: "",
        plantilla_id: "",
        item_number: "",
        eligibility_id: "",
        position_id: "",
        assigned_office_id: "",
        salary_grade_display: "",
        salary_grade: "",
        salary_step: "",
        base_salary: "",
        effectivity_date: "",
        //gov ids
        tin: "",
        gsis: "",
        sss: "",
        pagibig: "",
        philhealth: "",
      },
      dropdowns: {
        positions: [],
        plantillas: [],
        eligibilities: [],
        employmentStatuses: [],
        offices: [],
        salarySteps: [],
      },
      salaryAmount: '',

      //for errors
      errors: {
        //step 1
        profile_photo: null,
        username: "",
        email: "",
        cellphone_number: "",
        first_name: "",
        middle_name: "",
        last_name: "",
        birthday: "",
        sex: "",
        
        // =================================================
            //address - current/permanent
            region: "",
            province: "",
            city_or_municipality: "",
            barangay: "",
            address_line: "",
            //birthplace
            bp_region: "",
            bp_province: "",
            bp_city_or_municipality: "",
            bp_barangay: "",
            bp_address_line: "",
        // =================================================

        //step 2
        employment_status_id: "",
        employee_id_number: "",
        plantilla_id: "",
        item_number: "",
        eligibility_id: "",
        position_id: "",
        assigned_office_id: "",
        salary_grade_display: "",
        salary_grade: "",
        salary_step: "",
        base_salary: "",
        effectivity_date: "",
        //gov ids
        tin: "",
        gsis: "",
        pagibig: "",
        philhealth: "",
      },
      //for psgc api
      regions: [],
      provinces: [],
      cities_or_municipalities: [],
      barangays: [],

      bp_provinces: [],
      bp_cities: [],
      bp_barangays: [],

      validProvince: false,
      validBPProvince: false,

      nextStep: false,
      
    };
  },
  methods: {
    // for formatting numbers
    formatCurrency(amount) {
      if (amount === null || amount === undefined || amount === '') return '0.00';
      return Number(amount).toLocaleString('en-PH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    // for getting the text label in step 3 from dropdown IDs
    getDisplayValue(listName, id) {
        const list = this.dropdowns[listName];
        if (!list || !id) return 'N/A';

        const item = list.find(i => i.id === id);

        return item
            ? (item.name || item.title || 'N/A')
            : 'N/A';
    },

    //for upload photo preview...
    handlePhotoUpload(e) {
      const file = e.target.files[0];
        if (file) {
            this.employee.profile_photo = file;
            this.photoPreview = URL.createObjectURL(file);
        }
    },
    //for step validation before poceed...
    async validateStep() {
        this.nextStep = true;
        
        // Clear all errors first
        for(let key in this.errors) {
            this.errors[key] = '';
        }

        await api
        .post(`/validateStep`, this.employee, {
            params: {
                step: this.currentStep
            },
            headers: {
                'Content-Type': 'multipart/form-data' //to validate the photo...
            }
        })
        .then((res) => {
            if(res.status == 200){
                if(this.currentStep == 1){
                    this.employee.address = `${this.employee.address_line ? this.employee.address_line + ', ' : ''}${this.employee.barangay ? this.employee.barangay + ', ' : ''}${this.employee.city_or_municipality ? this.employee.city_or_municipality + ', ' : ''}${this.employee.province ? this.employee.province + ', ' : ''}${this.employee.region ? this.employee.region : ''}`;
                    this.employee.birthplace = `${this.employee.bp_address_line ? this.employee.bp_address_line + ', ' : ''}${this.employee.bp_barangay ? this.employee.bp_barangay + ', ' : ''}${this.employee.bp_city_or_municipality ? this.employee.bp_city_or_municipality + ', ' : ''}${this.employee.bp_province ? this.employee.bp_province + ', ' : ''}${this.employee.bp_region ? this.employee.bp_region : ''}`;
                }
                this.currentStep++;
            }
            this.nextStep = false;
        })
        .catch((err) => {
            let errormsg = err.response.data.errors;
            this.nextStep = false;

            if(errormsg) {
                // Map the returned errors dynamically
                for(let field in errormsg) {
                    this.errors[field] = errormsg[field][0];
                }

                // ======================================================================
                // AUTO FOCUS LOGIC: Find the first field with an error and focus on it!
                // ======================================================================
                this.$nextTick(() => {
                    const errorFields = Object.keys(errormsg);
                    if (errorFields.length > 0) {
                        
                        // Maps profile_photo to the photoInput ref
                        const firstFieldKey = errorFields[0] === 'profile_photo' ? 'profile_photo' : errorFields[0];
                        let targetElement = this.$refs[firstFieldKey];

                        if(Array.isArray(targetElement)) targetElement = targetElement[0];

                        if (targetElement) {
                            // Check if it's a hidden Materialize select box
                            if (targetElement.tagName && targetElement.tagName.toLowerCase() === 'select' && !targetElement.classList.contains('browser-default')) {
                                const wrapper = targetElement.closest('.select-wrapper');
                                if (wrapper) {
                                    const materializeInput = wrapper.querySelector('input.select-dropdown');
                                    if (materializeInput) {
                                        materializeInput.focus();
                                        materializeInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    }
                                }
                            } else {
                                // Standard input box
                                targetElement.focus();
                                targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                        }
                    }
                });
            }
        })
    },
    //fetch the datas for select inputs...
    async getListForDropdowns() {
        await api
        .get('/dropdowns-lists')
        .then((res) => {
            this.dropdowns.positions = res.data.positions;
            this.dropdowns.plantillas = res.data.plantillas;
            this.dropdowns.eligibilities = res.data.eligibilities;
            this.dropdowns.employmentStatuses = res.data.employmentStatuses;
            this.dropdowns.offices = res.data.offices;
            
            refreshSelectDropdown(this);
        })
        .catch(() => {
            console.log("An error occured...");
        })
    },
    //for position select, para sa salary grade...
    handlePositionChange(positionId) {

        //palitan yung laman ng sg-select
        let selectedPosition = "";
        for(let i = 0; i <this.dropdowns.positions.length; i++){
            let position = this.dropdowns.positions[i];

            if(position.id === positionId){
                selectedPosition = position;
                break;
            }
        }
        if (selectedPosition) {
            this.employee.salary_grade = selectedPosition.salary_grade_level;
            this.employee.salary_grade_display = this.employee.salary_grade;

            this.employee.salary_step = "";
            this.salaryAmount = "";
        }

        this.getSgSteps(this.employee.salary_grade_display);
    },
    // get steps for specific grade...
    async getSgSteps(salaryGrade) {
        await api
        .get("/get-salary-steps/" + salaryGrade)
        .then((res) => {
            this.dropdowns.salarySteps = res.data.steps;
        })
        .catch(() => {
            console.log("error occured while trying to get the steps...")
        })
            
        this.$nextTick(() => {
            window.M.FormSelect.init(document.getElementById('salary_step'));
        });
    },
    //for salary amount input
    loadSalaryAmount() {
        let salarySteps = this.dropdowns.salarySteps;
        for(let i = 0; i<salarySteps.length; i++){
            if(salarySteps[i].grade == this.employee.salary_grade && salarySteps[i].step == this.employee.salary_step) {
                this.salaryAmount = salarySteps[i].salary_amount;
                break;
            }
        }
    },
    // Fetch initial list of all regions
    async getRegions() {
        await externalApi.get("https://psgc.gitlab.io/api/regions/")
        .then((res) => {
            this.regions = res.data;
        })
        .catch((err) => { console.log(err); });
    },
    // Triggered when a Region is selected
    async handleRegionChange(type) {
        let rName = type === 'bp' ? this.employee.bp_region : this.employee.region;
        let region = this.regions.find(r => r.name === rName);
        
        // Reset child inputs automatically when parent changes
        if (type === 'bp') {
            this.bp_provinces = []; this.bp_cities = []; this.bp_barangays = [];
            this.employee.bp_province = ''; this.employee.bp_city_or_municipality = ''; this.employee.bp_barangay = '';
        } else {
            this.provinces = []; this.cities_or_municipalities = []; this.barangays = [];
            this.employee.province = ''; this.employee.city_or_municipality = ''; this.employee.barangay = '';
        }

        if(!region) return;

        await externalApi.get(`https://psgc.gitlab.io/api/regions/${region.code}/provinces/`)
        .then(async (res) => {
            let data = res.data;
            
            // Handle edge-case for NCR which has no provinces
            if (data.length === 0) {
                const cityRes = await externalApi.get(`https://psgc.gitlab.io/api/regions/${region.code}/cities-municipalities/`);
                data = [{ code: region.code, name: 'NCR' }]; // Inject dummy province
                if (type === 'bp') {
                    this.employee.bp_province = 'NCR';
                    this.bp_cities = cityRes.data;
                } else {
                    this.employee.province = 'NCR';
                    this.cities_or_municipalities = cityRes.data;
                }
            }
            
            if (type === 'bp') this.bp_provinces = data;
            else this.provinces = data;
        }).catch(err => console.log(err));
    },
    // Triggered when a Province is selected
    async handleProvinceChange(type) {
        let pName = type === 'bp' ? this.employee.bp_province : this.employee.province;
        let provList = type === 'bp' ? this.bp_provinces : this.provinces;
        let prov = provList.find(p => p.name === pName);

        if (type === 'bp') {
            this.bp_cities = []; this.bp_barangays = [];
            this.employee.bp_city_or_municipality = ''; this.employee.bp_barangay = '';
        } else {
            this.cities_or_municipalities = []; this.barangays = [];
            this.employee.city_or_municipality = ''; this.employee.barangay = '';
        }

        if(!prov) return;

        // Use Region code for NCR, otherwise use Province code
        let endpoint = prov.name === 'NCR' 
            ? `https://psgc.gitlab.io/api/regions/${prov.code}/cities-municipalities/`
            : `https://psgc.gitlab.io/api/provinces/${prov.code}/cities-municipalities/`;
            
        await externalApi.get(endpoint)
        .then((res) => {
            if (type === 'bp') this.bp_cities = res.data;
            else this.cities_or_municipalities = res.data;
        }).catch(err => console.log(err));
    },
    // Triggered when a City/Municipality is selected
    async handleCityChange(type) {
        let cName = type === 'bp' ? this.employee.bp_city_or_municipality : this.employee.city_or_municipality;
        let cityList = type === 'bp' ? this.bp_cities : this.cities_or_municipalities;
        let city = cityList.find(c => c.name === cName);

        if (type === 'bp') {
            this.bp_barangays = []; this.employee.bp_barangay = '';
        } else {
            this.barangays = []; this.employee.barangay = '';
        }

        if(!city) return;

        await externalApi.get(`https://psgc.gitlab.io/api/cities-municipalities/${city.code}/barangays/`)
        .then((res) => {
            if (type === 'bp') this.bp_barangays = res.data;
            else this.barangays = res.data;
        }).catch(err => console.log(err));
    },
    //for mobile numbeer formats and ids
    formatNumber(value, pattern) {
        // remove non-digits
        let numbers = value.replace(/\D/g, "");

        let result = "";
        let numIndex = 0;

        for (let i = 0; i < pattern.length; i++) {

        if (pattern[i] === "0") {
            if (numbers[numIndex]) {
            result += numbers[numIndex];
            numIndex++;
            }
        } 
        else {
            if (numbers[numIndex]) {
            result += pattern[i];
            }
        }

        }

        return result;
    },
    //for submitting the datas...
    async addEmployee() {
        this.loading = true;
        let formData = new FormData();

        for (let key in this.employee) {
            formData.append(key, this.employee[key]);
        }

        await api.post('/employee', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Accept': 'application/json',
            }
        })
        .then(() => {
            window.M.toast({html: 'Employee successfully saved!', classes: 'green rounded'});
            this.$router.push('/employees');
        })
        .catch((err) => {
            console.log(err.response);
            window.M.toast({html: 'Error saving employee.', classes: 'red rounded'});
            this.loading = false;
        });
    },
    //capitalize word on input
    capitalize(field) {
        let value = this.employee[field];

        if (!value) return;

        this.employee[field] = value
        .toUpperCase();
    },
  },
  mounted() {
    this.getListForDropdowns();
    this.getRegions();
  },
  watch: {
    'employee.employment_status_id'(value) {
        if(value == 3){
            this.employee.item_number = '';
            this.employee.salary_grade = "";
            this.employee.salary_grade_display = "";
            this.employee.salary_step = "";
            this.employee.position_id = "";
            this.employee.plantilla_id = "";
            this.employee.gsis = ""; // Auto-clear hidden GSIS field
            
            this.errors.item_number = "";
            this.errors.position_id = "";
            this.errors.plantilla_id = "";
            this.errors.gsis = "";

            this.$nextTick(() => {
                if(document.getElementById('salary_grade_display')) window.M.FormSelect.init(document.getElementById('salary_grade_display'));
                if(document.getElementById('job_title')) window.M.FormSelect.init(document.getElementById('job_title'));
                if(document.getElementById('salary_step')) window.M.FormSelect.init(document.getElementById('salary_step'));
                if(document.getElementById('plantilla')) window.M.FormSelect.init(document.getElementById('plantilla'));
            });
        }else{
            this.employee.item_number = "";
            
            this.$nextTick(() => {
                if(document.getElementById('job_title')) window.M.FormSelect.init(document.getElementById('job_title'));
                if(document.getElementById('plantilla')) window.M.FormSelect.init(document.getElementById('plantilla'));
            });
        }
    }
  },
};
</script>

<style scoped>
    #loader {
        background-color: white;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        z-index: 10;
        width: 100%;

        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    #stepContent {
        position: relative;
        transition: opacity 0.3s ease;
    }


    #sgDiv,
    #bsDiv {
        transition: all 0s !important;
        transition-delay: 0s !important;
    }

    .input-field label>span {
        position: absolute !important;
        width: 100% !important;
    }

    /* Clean Table Styling for Step 3 Cards */
    .clean-table td, .clean-table th { 
        padding: 8px 5px; 
        border-radius: 0; 
    }

    /* Loading Dots Animation */
    .dots-loader span {
        animation: blink 1.4s infinite both;
        font-size: 1.5rem;
        font-weight: bold;
        line-height: 1;
    }

    .dots-loader span:nth-child(2) {
        animation-delay: 0.2s;
    }

    .dots-loader span:nth-child(3) {
        animation-delay: 0.4s;
    }

    @keyframes blink {
        0% { opacity: 0.2; }
        20% { opacity: 1; }
        100% { opacity: 0.2; }
    }

    .btn-animated {
        transition: background-color 0.3s ease, transform 0.1s ease;
    }
    
    .btn-animated:disabled {
        background-color: #90caf9 !important;
        color: white !important;
        cursor: not-allowed;
    }
</style>

<style>
    /* Un-scoped styles specifically for Materialize's generated DOM */
    .input-field.has-error input.select-dropdown {
        border-bottom: 1px solid #F44336 !important;
        box-shadow: 0 1px 0 0 #F44336 !important;
    }

    .input-field.is-valid input.select-dropdown {
        border-bottom: 1px solid #4CAF50 !important;
        box-shadow: 0 1px 0 0 #4CAF50 !important;
    }
</style>